const RAIN_CODES = new Set([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82]);
const SNOW_CODES = new Set([71, 73, 75, 77, 85, 86]);
const STORM_CODES = new Set([95, 96, 99]);

const isWetWeatherCode = (weatherCode) =>
  RAIN_CODES.has(weatherCode) || SNOW_CODES.has(weatherCode) || STORM_CODES.has(weatherCode);

export const isDayForIt = (currentWeather) => {
  if (!currentWeather) return { isDayForIt: false, reason: 'Not a day for it. No data.' };

  const temperature = currentWeather.temperature_2m;
  const apparentTemperature = currentWeather.apparent_temperature ?? temperature;
  const weatherCode = currentWeather.weather_code;
  const windSpeed = currentWeather.wind_speed_10m ?? 0;
  const windGusts = currentWeather.wind_gusts_10m ?? windSpeed;
  const precipitation = currentWeather.precipitation ?? 0;
  const cloudCover = currentWeather.cloud_cover ?? 0;
  const uvIndex = currentWeather.uv_index ?? 0;
  const humidity = currentWeather.relative_humidity_2m ?? null;
  const isDaytime = currentWeather.is_day ?? 1;

  if (isWetWeatherCode(weatherCode) || precipitation >= 0.3) {
    return {
      isDayForIt: false,
      reason: 'Not a day for it. Wet weather kills park beers.',
    };
  }

  if (weatherCode === 45 || weatherCode === 48) {
    return {
      isDayForIt: false,
      reason: 'Not a day for it. Too bleak for park beers.',
    };
  }

  if (apparentTemperature < 14) {
    return {
      isDayForIt: false,
      reason: 'Not a day for it. Too chilly to sit in the park with a beer.',
    };
  }

  if (apparentTemperature > 34) {
    return {
      isDayForIt: false,
      reason: 'Not a day for it. Too hot to enjoy park beers properly.',
    };
  }

  if (windSpeed >= 28 || windGusts >= 40) {
    return {
      isDayForIt: false,
      reason: 'Not a day for it. Too windy for a relaxed park beer.',
    };
  }

  if (isDaytime === 1 && uvIndex >= 10 && apparentTemperature >= 28) {
    return {
      isDayForIt: false,
      reason: 'Not a day for it. The sun is doing too much for park beers.',
    };
  }

  let comfortScore = 100;

  if (apparentTemperature < 18) comfortScore -= 12;
  else if (apparentTemperature > 29) comfortScore -= 16;

  if (windSpeed >= 18) comfortScore -= 12;
  if (windGusts >= 30) comfortScore -= 8;
  if (cloudCover >= 85) comfortScore -= 4;
  if (humidity !== null && humidity >= 80 && apparentTemperature >= 26) comfortScore -= 10;
  if (isDaytime === 1 && uvIndex >= 8 && apparentTemperature >= 26) comfortScore -= 8;
  if (isDaytime === 0 && apparentTemperature < 18) comfortScore -= 6;

  if (comfortScore >= 72) {
    if (windSpeed >= 18 || windGusts >= 30) {
      return {
        isDayForIt: true,
        reason: 'Day for it. A touch breezy, but still good enough for park beers.',
      };
    }

    if (cloudCover >= 85) {
      return {
        isDayForIt: true,
        reason: 'Day for it. A bit grey, but still solid park-beer weather.',
      };
    }

    if (isDaytime === 0) {
      return {
        isDayForIt: true,
        reason: 'Day for it. Still pleasant enough for a twilight park beer.',
      };
    }

    return {
      isDayForIt: true,
      reason: 'Day for it. Warm, dry, and calm enough for park beers.',
    };
  }

  if (apparentTemperature > 29) {
    return {
      isDayForIt: false,
      reason: 'Not a day for it. A bit too hot and sticky for park beers.',
    };
  }

  if (windSpeed >= 18 || windGusts >= 30) {
    return {
      isDayForIt: false,
      reason: 'Not a day for it. Too gusty to properly enjoy park beers.',
    };
  }

  if (isDaytime === 0) {
    return {
      isDayForIt: false,
      reason: 'Not a day for it. The park-beer window has mostly closed.',
    };
  }

  return {
    isDayForIt: false,
    reason: 'Not a day for it. Not quite pleasant enough for beers in the park.',
  };
};
